import { useTranslation } from "react-i18next";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  Icon,
  Skeleton,
  cn,
} from "@nexus/ui";
import type { Vendor } from "../types/marketplace.types";
import { getVendorInitials } from "../lib/vendorInitials";
import { formatCadenceLabel } from "../lib/package-cadence";
import { useConnectWizardStore } from "../store/connectWizard.store";

const MAX_VISIBLE_PACKAGES = 3;

function formatPackageAmount(price: number, currency: string | null): string {
  if (currency) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency,
        maximumFractionDigits: 0,
      }).format(price);
    } catch {
      // Currency wasn't a valid ISO 4217 code (admin free-text) — fall back
      // to a plain, grouped amount prefixed with the raw code.
      return `${currency} ${price.toLocaleString()}`;
    }
  }
  return price.toLocaleString();
}

export interface VendorCardProps {
  vendor: Vendor;
}

export function VendorCard({ vendor }: VendorCardProps) {
  const { t } = useTranslation();
  const openWizard = useConnectWizardStore((s) => s.openWizard);
  const activePackages = vendor.packages.filter((p) => p.isActive);
  const visiblePackages = activePackages.slice(0, MAX_VISIBLE_PACKAGES);
  const hiddenCount = activePackages.length - visiblePackages.length;
  const initials = getVendorInitials(vendor.name);

  return (
    <Card
      className={cn(
        "group/vendor-card flex h-full flex-col gap-0 overflow-hidden py-0 shadow-sm",
        "transition-[box-shadow,border-color] duration-150 ease-out",
        "hover:border-primary/30 hover:shadow-md",
      )}
    >
      <CardHeader
        className={cn(
          "flex flex-row items-start gap-3.5 pt-5",
          activePackages.length > 0 ? "pb-4" : "pb-5",
        )}
      >
        {vendor.logoUrl ? (
          <img
            src={vendor.logoUrl}
            alt=""
            loading="lazy"
            className="size-11 shrink-0 rounded-xl border border-border/60 bg-background object-contain p-1 transition-colors duration-150 group-hover/vendor-card:border-primary/30"
          />
        ) : (
          <div
            aria-hidden="true"
            className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-sm font-semibold tracking-wide text-primary ring-1 ring-inset ring-primary/15"
          >
            {initials}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <h3 className="line-clamp-2 text-[15px] font-semibold leading-snug tracking-[-0.01em] text-foreground [overflow-wrap:anywhere]">
            {vendor.name}
          </h3>
          {vendor.description && (
            <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-muted-foreground [overflow-wrap:anywhere]">
              {vendor.description}
            </p>
          )}
          {vendor.websiteUrl && (
            <a
              href={vendor.websiteUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t("marketplace.catalog.visitWebsiteAria", {
                name: vendor.name,
              })}
              className="mt-2 -ml-1 inline-flex items-center gap-1 rounded px-1 py-0.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
            >
              {t("marketplace.catalog.visitWebsite")}
              <Icon name="external-link" size="xs" aria-hidden />
            </a>
          )}
        </div>
      </CardHeader>

      {activePackages.length > 0 && (
        <CardContent className="flex-1 pt-0 pb-4">
          <p className="mb-2.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/70">
            {t("marketplace.catalog.packages")}
          </p>
          <ul className="flex flex-col gap-2.5">
            {visiblePackages.map((pkg) => (
              <li
                key={pkg.id}
                className="flex items-center justify-between gap-3"
              >
                <span className="flex min-w-0 items-center gap-2">
                  <span className="truncate text-[13px] text-foreground">
                    {pkg.name}
                  </span>
                  {pkg.isPopular && (
                    <Badge className="shrink-0 bg-primary/10 text-primary">
                      {t("marketplace.catalog.popular")}
                    </Badge>
                  )}
                </span>
                {pkg.price !== null && (
                  <span className="flex shrink-0 items-baseline gap-1 whitespace-nowrap">
                    <span className="text-[13px] font-semibold tabular-nums text-foreground">
                      {formatPackageAmount(pkg.price, pkg.currency)}
                    </span>
                    {formatCadenceLabel(pkg.cadence, t) && (
                      <span className="text-[11px] text-muted-foreground">
                        · {formatCadenceLabel(pkg.cadence, t)}
                      </span>
                    )}
                  </span>
                )}
              </li>
            ))}
          </ul>
          {hiddenCount > 0 && (
            <p className="mt-2 text-[11px] text-muted-foreground">
              {t("marketplace.catalog.morePackages", { count: hiddenCount })}
            </p>
          )}
        </CardContent>
      )}

      <CardFooter className="mt-auto border-t border-border/50 bg-muted/15 px-5 py-3">
        <Button
          type="button"
          className="w-full cursor-pointer"
          onClick={() => openWizard(vendor.id)}
        >
          {t("marketplace.catalog.connect")}
        </Button>
      </CardFooter>
    </Card>
  );
}

export function VendorCardSkeleton() {
  return (
    <Card
      data-testid="vendor-card-skeleton"
      className="flex h-full flex-col gap-0 overflow-hidden py-0"
    >
      <CardHeader className="flex flex-row items-start gap-3.5 pb-4 pt-5">
        <Skeleton className="size-11 shrink-0 rounded-xl" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-2/5" />
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-4/5" />
        </div>
      </CardHeader>
      <CardContent className="flex-1 pb-4 pt-0">
        <Skeleton className="mb-3 h-2.5 w-16" />
        <div className="flex flex-col gap-2.5">
          <div className="flex items-center justify-between">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-3 w-12" />
          </div>
          <div className="flex items-center justify-between">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-3 w-10" />
          </div>
        </div>
      </CardContent>
      <CardFooter className="mt-auto border-t border-border/50 bg-muted/15 px-5 py-3">
        <Skeleton className="h-8 w-full rounded-md" />
      </CardFooter>
    </Card>
  );
}
